/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect } from 'react';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import { useProject } from '@jira/navigation-apps__sidebar-common/src/controllers/project/index.tsx';
import { usePrevious } from '@jira/platform__utils-react-hooks-use-previous/src/common/utils/index.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createStore,
	type Action,
	type BoundActions,
	type HookFunction,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { actions } from './actions/index.tsx';
import type { State } from './types.tsx';

type PartialOrNull<T> = {
	[P in keyof T]?: T[P] | null;
};

export type Actions = typeof actions;

export const initialState: State = {};

const Store = createStore<State, Actions>({
	name: 'project-properties',
	initialState,
	actions,
});

/**
 * Exported for testing purposes only.
 * Use `useProjectProperties` instead.
 */
export const useProjectPropertiesStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			get: (projectId: string, properties: string[]) => Action<State>;
		}
	>,
	void
> = createHook(Store);

export const useProjectProperties = <ReturnType extends Record<string, unknown>>(
	propertyKeys: (keyof ReturnType)[],
	projectIdOverride?: string,
): PartialOrNull<ReturnType> => {
	const { data } = useProject();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectId = projectIdOverride !== undefined ? projectIdOverride : data?.id && `${data.id}`;

	const [state, { get }] = useProjectPropertiesStore();
	const prevPropertyKeys = usePrevious(propertyKeys);

	useEffect(() => {
		if (projectId === undefined) {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'jiraProjectPropertiesServices undefinedProjectId',
			);
		}
		if (isEqual(propertyKeys, prevPropertyKeys) && !projectIdOverride) {
			return;
		}
		if (projectId) {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			get(projectId, propertyKeys as string[]);
		}
	}, [projectId, propertyKeys, prevPropertyKeys, get, createAnalyticsEvent, projectIdOverride]);

	return projectId !== undefined
		? // oxlint-disable-next-line typescript/consistent-type-assertions
			(pickBy(state[projectId], (_, key: string) =>
				includes(propertyKeys, key),
			) as PartialOrNull<ReturnType>)
		: {};
};

const useProjectPropertiesGated = functionWithCondition(
	() => fg('initial-project-template-event-attr'),
	useProjectProperties,
	() => ({}),
);

export const useInitialProjectTemplate = (
	overrideProjectId?: string,
): string | undefined | null => {
	const { 'initial.project.template.name': initialProjectTemplate } = useProjectPropertiesGated<{
		'initial.project.template.name': string | undefined | null;
	}>(['initial.project.template.name'], overrideProjectId);

	if (fg('initial-project-template-event-attr')) {
		return initialProjectTemplate;
	}

	return undefined;
};
